import { useState, useRef, useMemo, useEffect, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'

import {
  useExportableChart,
  svgToPngDataUrl
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'

import {
  waitForShelfImage,
  appendShelfImageInto,
  applySvgHeatmapStyles,
  applyImageScaleStyles
} from '@aytm/app/stats/page/question-views/sh/components/shelf/lib/imageLoader'
import { calcHeatmapProps } from '@aytm/app/stats/page/question-views/sh/components/shelf/lib/heatmapProps'
import {
  downloadShelfAs,
  formatSVG,
  toDataUrl
} from '@aytm/app/stats/page/question-views/sh/components/shelf/lib/downloadShelf'
import { loadImageAsBase64 } from '@aytm/app/stats/page/question-views/hm/components/chart/components/action-buttons/utils'
import Heatmap from '@aytm/app/stats/page/question-views/sh/components/shelf/Heatmap'
import ShelfNote from '@aytm/app/stats/page/question-views/sh/components/shelf/ShelfNote'
import Preloader from '@aytm/app/stats/page/question-views/components/Preloader'
import { useRenderGate } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/render-gate'

import style from '@aytm/app/stats/page/question-views/sh/components/shelf/styles/ShelfChart.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const EXPORT_OPTIONS = [
  { key: 'png', text: 'Download PNG image' },
  { key: 'jpeg', text: 'Download JPEG image' },
  { key: 'pdf', text: 'Download PDF document' },
  { key: 'eps', text: 'Download EPS file' }
]

const ShelfChart = ({
  id,
  questionKey,
  shelfData,
  shelfType,
  shelfViewMode,
  shelfAnswer,
  preview,
  onComplete,
  onChartReady
}) => {
  const heatmapRef = useRef(null)
  const apiRef = useRef({})
  const [image, setImage] = useState(null)

  // the shelf is done when its image is in and the layout passes have settled -- the same moment
  // the block is told to drop its loader
  const chartRendered = useRenderGate()

  const isEcommerce = shelfData?.store === 'ecommerce'

  // Seed `window.AYTM_shelfImage_<id>` from preview, then poll until its `src` is set.
  // Dep on `preview.original.path` (primitive) — Backbone re-emits a fresh `preview`
  // object on every render, which would loop the effect.
  const previewPath = preview?.original?.path
  useEffect(() => {
    if (!shelfData || isEcommerce) {
      onComplete?.(true)
      chartRendered()
      return
    }
    if (!PageGlobals.PREVIEW_TOKEN_PATH) return

    if (preview?.original) {
      window[`AYTM_shelfImage_${shelfData.id}`] = preview.original
      window[`AYTM_shelfImage_${shelfData.id}`].src = preview.original.path
    }

    return waitForShelfImage(shelfData.id, img => setImage(img))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shelfData?.id, previewPath, isEcommerce, onComplete])

  // Two-pass layout: append image first, then style — the second pass needs
  // both the appended <img> and the mounted <Heatmap> SVG in the DOM.
  useLayoutEffect(() => {
    if (!image || !heatmapRef.current) return
    appendShelfImageInto(heatmapRef.current, image)
  }, [image])

  useLayoutEffect(() => {
    if (!image || !heatmapRef.current) return

    applySvgHeatmapStyles(heatmapRef.current, image)

    const fullscreenScale = window[`AYTM_shelfScale_${shelfData.id}`] || 1

    applyImageScaleStyles(heatmapRef.current, shelfData.id, fullscreenScale)
  }, [image, shelfData?.id, shelfViewMode, shelfAnswer])

  // 300ms delay lets the two layout passes settle before the parent removes the loader.
  useEffect(() => {
    if (!image) return
    const t = setTimeout(() => {
      onComplete?.(true)
      chartRendered()
    }, 300)
    return () => clearTimeout(t)
  }, [image, onComplete, chartRendered])

  const heatmapProps = useMemo(() => {
    if (!image) return null
    return calcHeatmapProps({
      shelfId: id,
      shelfType,
      shelfViewMode,
      shelfAnswer,
      imageWidth: image.width,
      imageHeight: image.height,
      fullscreenScale: window[`AYTM_shelfScale_${shelfData.id}`] || 1
    })
  }, [id, image, shelfData?.id, shelfType, shelfViewMode, shelfAnswer])

  // Headless export: single-output, legacy parity — pptx generates ONE PNG for sh
  // (the shelf composite); ProductMetrics is metadata-only there. Reuses the live
  // download path (toDataUrl → formatSVG on the mounted #svg-heatmap{id}). `deps:
  // [image]` also keeps the hook inert for e-commerce, where image never loads.
  useExportableChart({
    deps: [image],
    prepareForExport: async () => {
      const img = window[`AYTM_shelfImage_${shelfData.id}`]
      if (!img?.src) throw new Error('Shelf: image not loaded')
      // The live download's direct GCS fetch (toDataUrl) is CORS-blocked on the
      // render route — go through the asset service's CORS-safe copy, as hm does.
      if (!img.baseSrc) img.baseSrc = await loadImageAsBase64(img.path || img.src)
      if (!img.baseSrc) throw new Error('Shelf: image fetch failed')
      const svgData = formatSVG({ id, shelfData, shelfType, image: img })
      if (!svgData) throw new Error('Shelf: no composite to export')
      return [await svgToPngDataUrl(svgData, img.width, img.height)]
    }
  })

  // PPTX bridge (pptx.coffee -> block.getCanvasFromSVG). The composite is built here, reusing
  // the very helpers the live Download button uses; coffee only rasterises the result. Async
  // because the source image has to be inlined as base64 before it can go into the svg.
  apiRef.current.getExportSvg = async () => {
    const img = window[`AYTM_shelfImage_${shelfData?.id}`]
    if (!img?.src) return null

    if (!img.baseSrc) await toDataUrl(img, `https://${img.src}`)
    if (!img.baseSrc) return null

    return formatSVG({ id, shelfData, shelfType, image: img }) || null
  }

  apiRef.current.getExportSize = () => {
    const img = window[`AYTM_shelfImage_${shelfData?.id}`]
    return img ? { width: img.width, height: img.height } : null
  }

  useEffect(() => {
    onChartReady?.(apiRef.current)
  }, [onChartReady])

  const handleExport = async (fileType, done) => {
    try {
      await downloadShelfAs({
        fileType,
        position: questionKey,
        model: { id, shelf_preview_data: shelfData, shelfType }
      })
    } finally {
      done?.()
    }
  }

  if (isEcommerce) return null

  const shouldRenderHeatmap = image && heatmapProps && shelfViewMode !== 'shelfs' && shelfAnswer

  return (
    <div className="sh-view">
      <div className={style.export}>
        <ExportDropdown options={EXPORT_OPTIONS} onExport={handleExport} />
      </div>
      {/* The PPTX export gates the shelf image on finding this id. */}
      <div ref={heatmapRef} id={`shelf_chart_${id}`} className={style.heatmap}>
        {shouldRenderHeatmap && <Heatmap {...heatmapProps} />}
        {image ? null : <Preloader />}
      </div>
      <ShelfNote />
    </div>
  )
}

ShelfChart.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  questionKey: PropTypes.string,
  shelfData: PropTypes.object,
  shelfType: PropTypes.string,
  shelfViewMode: PropTypes.string,
  shelfAnswer: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  preview: PropTypes.object,
  onComplete: PropTypes.func,
  onChartReady: PropTypes.func
}

ShelfChart.displayName = 'ShelfChart'

export default ShelfChart
